import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getTransactions,
  searchTransactions,
  fetchAllTransactionsForExport,
  TransactionListOptions,
} from "@/services/firebase/transaction";
import { getDramas } from "@/services/firebase/drama";
import { Transaction } from "@/types/transaction";
import { useDebounce } from "@/hooks/useDebounce";
import CopyableId from "@/components/common/CopyableId";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { exportToCSV } from "@/utils/csvExport";
import { Download, X, Filter, Check, Loader2 } from "lucide-react";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminSearchInput } from "@/components/admin-ui/admin-search-input";
import {
  AdminDataTable,
  AdminTableColumn,
} from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminLoadingOverlay } from "@/components/admin-ui/admin-loading-overlay";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { AdminPagination } from "@/components/admin-ui/admin-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import FinancialDateRangePicker from "@/components/financial/FinancialDateRangePicker";
import { useFinancialDateRange } from "@/store/financialDateRange";

// 交易類型標籤配置
type BadgeTone = "info" | "success" | "warning" | "danger" | "neutral" | "secondary" | "purple";
const typeConfig: Record<
  Transaction["type"],
  { label: string; tone: BadgeTone }
> = {
  purchase: { label: "購買金幣", tone: "info" },
  unlock: { label: "解鎖影片", tone: "secondary" },
  full_drama_coin_purchase: { label: "全劇購買（金幣）", tone: "secondary" },
  admin_grant: { label: "後台發放", tone: "purple" },
  gift_code: { label: "兌換禮物代碼", tone: "warning" },
  daily_checkin: { label: "每日簽到", tone: "success" },
  mission_reward: { label: "任務獎勵", tone: "success" },
  app_usage_reward: { label: "App 使用獎勵", tone: "success" },
  ad_reward: { label: "觀看廣告獎勵", tone: "success" },
  refund: { label: "退款", tone: "danger" },
  adjustment: { label: "系統調整", tone: "neutral" },
};

const DRAMA_RELATED_TYPES: Transaction["type"][] = [
  "unlock",
  "full_drama_coin_purchase",
];

interface TransactionFilters {
  type?: Transaction["type"];
  types?: Transaction["type"][];
  drama_id?: string;
}

const PAGE_SIZE = 20;
const EXPORT_MAX_RECORDS = 10000;

const columns: AdminTableColumn[] = [
  { key: "type", label: "交易類型" },
  { key: "user_id", label: "用戶 ID" },
  { key: "amount", label: "金額", sortable: true },
  { key: "company_payout_ntd", label: "分潤 NTD", hideBelow: "md", sortable: true },
  { key: "info", label: "相關資訊", hideBelow: "md" },
  { key: "transaction_id", label: "交易 ID", hideBelow: "md" },
  { key: "created_at", label: "交易時間", sortable: true },
];

function filtersAreEqual(a: TransactionFilters, b: TransactionFilters): boolean {
  return (
    a.type === b.type &&
    a.drama_id === b.drama_id &&
    JSON.stringify(a.types ?? []) === JSON.stringify(b.types ?? [])
  );
}

export default function TransactionListPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  // 草稿 vs 已套用：使用者調整輸入只更新草稿，按「套用篩選」才真正觸發查詢
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<TransactionFilters>({});
  const [hasSearched, setHasSearched] = useState(false);

  // 全域日期區間（chip 點下去秒生效，不走 draft）
  const { start: dateRangeStart, end: dateRangeEnd } = useFinancialDateRange();
  const filterStartDate = useMemo(
    () => (dateRangeStart ? new Date(dateRangeStart) : undefined),
    [dateRangeStart]
  );
  const filterEndDate = useMemo(
    () => (dateRangeEnd ? new Date(`${dateRangeEnd}T23:59:59.999`) : undefined),
    [dateRangeEnd]
  );

  // 分頁狀態
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData>>>(new Map());

  // 日期區間變動時重置分頁
  useEffect(() => {
    setCurrentPage(1);
    pageCursorsRef.current.clear();
  }, [dateRangeStart, dateRangeEnd]);

  // 匯出狀態
  const [isExporting, setIsExporting] = useState(false);

  // 載入劇集清單（用於劇集篩選）
  const { data: dramas = [] } = useQuery({
    queryKey: ["dramas"],
    queryFn: getDramas,
  });

  // 搜尋查詢
  const {
    data: searchResults,
    isLoading: isSearching,
    error: searchError,
    refetch: refetchSearch,
  } = useQuery({
    queryKey: ["transactions", "search", debouncedSearchQuery],
    queryFn: () => searchTransactions(debouncedSearchQuery),
    enabled: false,
  });

  // 列表查詢（分頁）
  const {
    data: transactionResult,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      "transactions",
      "list",
      appliedFilters.type,
      appliedFilters.types,
      appliedFilters.drama_id,
      filterStartDate?.getTime(),
      filterEndDate?.getTime(),
      currentPage,
    ],
    queryFn: async () => {
      const options: TransactionListOptions = {
        filters: {
          type: appliedFilters.type,
          types: appliedFilters.types,
          drama_id: appliedFilters.drama_id,
          startDate: filterStartDate,
          endDate: filterEndDate,
        },
        limit: PAGE_SIZE,
      };
      if (currentPage > 1) {
        const cursor = pageCursorsRef.current.get(currentPage);
        if (cursor) {
          options.startAfter = cursor;
        }
      }
      return getTransactions(options);
    },
    enabled: !hasSearched || !debouncedSearchQuery.trim(),
    placeholderData: (prev) => prev,
  });

  // 同步分頁狀態
  useEffect(() => {
    if (transactionResult) {
      setHasMore(transactionResult.hasMore);
      if (transactionResult.lastDoc) {
        pageCursorsRef.current.set(currentPage + 1, transactionResult.lastDoc);
      }
    }
  }, [transactionResult, currentPage]);

  const handlePageNext = useCallback(() => {
    if (hasMore) {
      setCurrentPage((p) => p + 1);
    }
  }, [hasMore]);

  const handlePagePrev = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setHasSearched(false);
      return;
    }
    setHasSearched(true);
    await refetchSearch();
  };

  // 顯示資料
  const displayTransactions = useMemo(() => {
    if (hasSearched && debouncedSearchQuery.trim() && searchResults) {
      return searchResults;
    }
    return transactionResult?.transactions ?? [];
  }, [hasSearched, debouncedSearchQuery, searchResults, transactionResult]);

  const getTypeLabel = (type: Transaction["type"]): string => {
    return typeConfig[type]?.label || type;
  };

  const getTypeTone = (type: Transaction["type"]): BadgeTone => {
    return typeConfig[type]?.tone || "neutral";
  };

  const formatAmount = (amount: number): string => {
    return amount >= 0 ? `+${amount}` : `${amount}`;
  };

  const toCsvRow = (tx: Transaction) => ({
    交易類型: getTypeLabel(tx.type),
    用戶ID: tx.user_id,
    金額: tx.amount,
    劇集名稱: tx.drama_title || "",
    集數: tx.episode ?? "",
    PaidCoin使用: tx.paid_coin_used ?? "",
    FreeCoin使用: tx.free_coin_used ?? "",
    Paid費率: tx.paid_coin_rate_applied ?? "",
    Free費率: tx.free_coin_rate_applied ?? "",
    分潤NTD: tx.company_payout_ntd ?? "",
    產品ID: tx.product_id || "",
    平台: tx.platform || "",
    交易ID: tx.transaction_id,
    建立時間: tx.created_at ?? new Date(),
  });

  const csvHeaders = [
    { key: "交易類型" as const, label: "交易類型" },
    { key: "用戶ID" as const, label: "用戶 ID" },
    { key: "金額" as const, label: "金額" },
    { key: "劇集名稱" as const, label: "劇集名稱" },
    { key: "集數" as const, label: "集數" },
    { key: "PaidCoin使用" as const, label: "Paid Coin" },
    { key: "FreeCoin使用" as const, label: "Free Coin" },
    { key: "Paid費率" as const, label: "Paid 費率" },
    { key: "Free費率" as const, label: "Free 費率" },
    { key: "分潤NTD" as const, label: "分潤 NTD" },
    { key: "產品ID" as const, label: "產品 ID" },
    { key: "平台" as const, label: "平台" },
    { key: "交易ID" as const, label: "交易 ID" },
    { key: "建立時間" as const, label: "建立時間" },
  ];

  const buildFilename = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `交易記錄_${year}${month}${day}_${hours}${minutes}${seconds}`;
  };

  // 匯出 CSV（搜尋模式匯出搜尋結果；列表模式匯出全部篩選後的資料）
  const handleExportCSV = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      if (hasSearched && debouncedSearchQuery.trim() && searchResults) {
        if (searchResults.length === 0) {
          alert("沒有資料可以匯出");
          return;
        }
        exportToCSV(searchResults.map(toCsvRow), csvHeaders, buildFilename());
        return;
      }

      const { transactions, truncated, total } = await fetchAllTransactionsForExport(
        {
          type: appliedFilters.type,
          types: appliedFilters.types,
          drama_id: appliedFilters.drama_id,
          startDate: filterStartDate,
          endDate: filterEndDate,
        },
        { maxRecords: EXPORT_MAX_RECORDS }
      );

      if (total === 0) {
        alert("沒有資料可以匯出");
        return;
      }

      if (truncated) {
        const proceed = window.confirm(
          `篩選結果超過 ${EXPORT_MAX_RECORDS.toLocaleString()} 筆，僅會匯出最新的 ${total.toLocaleString()} 筆。\n建議使用更嚴格的篩選條件（例如限縮日期範圍）。\n\n仍要繼續匯出？`
        );
        if (!proceed) return;
      }

      exportToCSV(transactions.map(toCsvRow), csvHeaders, buildFilename());
    } catch (err) {
      console.error("匯出 CSV 失敗:", err);
      alert("匯出 CSV 時發生錯誤，請稍後再試");
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    hasSearched,
    debouncedSearchQuery,
    searchResults,
    appliedFilters,
  ]);

  // 套用篩選：把草稿搬到 applied，重置分頁
  const applyFilters = useCallback(() => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
  }, [draftFilters]);

  // 清除所有篩選（包含搜尋）
  const handleClearFilters = () => {
    setDraftFilters({});
    setAppliedFilters({});
    setSearchQuery("");
    setHasSearched(false);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    setHasMore(false);
  };

  // 下拉選單類別變更：保持自動套用（避免多次操作體驗不好）
  const updateCategoricalFilter = (
    patch: Partial<TransactionFilters>
  ) => {
    const next = { ...draftFilters, ...patch };
    setDraftFilters(next);
    setAppliedFilters(next);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
  };

  const currentTypeFilterValue = useMemo(() => {
    if (
      draftFilters.types &&
      draftFilters.types.length === DRAMA_RELATED_TYPES.length &&
      DRAMA_RELATED_TYPES.every((t) => draftFilters.types!.includes(t))
    ) {
      return "drama_related";
    }
    return draftFilters.type || "all";
  }, [draftFilters.type, draftFilters.types]);

  const hasDirtyFilters = !filtersAreEqual(draftFilters, appliedFilters);
  const hasAnyAppliedFilter =
    appliedFilters.type != null ||
    (appliedFilters.types?.length ?? 0) > 0 ||
    appliedFilters.drama_id != null;

  const renderCell = (transaction: Transaction, column: AdminTableColumn) => {
    switch (column.key) {
      case "type":
        return (
          <Badge
            variant={getTypeTone(transaction.type)}
            className="text-xs font-semibold min-w-[100px] justify-center"
          >
            {getTypeLabel(transaction.type)}
          </Badge>
        );
      case "user_id":
        return <CopyableId id={transaction.user_id} maxLength={12} />;
      case "amount":
        return (
          <span
            className={`text-sm font-semibold ${
              transaction.amount >= 0
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {formatAmount(transaction.amount)}
          </span>
        );
      case "company_payout_ntd":
        if (transaction.company_payout_ntd == null) {
          return <span className="text-muted-foreground/50">—</span>;
        }
        return (
          <span className="text-sm tabular-nums text-amber-700 dark:text-amber-400">
            {transaction.company_payout_ntd.toFixed(2)}
          </span>
        );
      case "info":
        return (
          <div className="text-sm text-muted-foreground">
            {transaction.drama_title && (
              <div>
                劇集：{transaction.drama_title}
                {transaction.episode && ` 第${transaction.episode}集`}
              </div>
            )}
            {transaction.product_id && (
              <div>產品 ID: {transaction.product_id}</div>
            )}
            {transaction.platform && (
              <div>平台: {transaction.platform}</div>
            )}
            {!transaction.drama_title &&
              !transaction.product_id &&
              !transaction.platform && (
                <span className="text-muted-foreground/50">-</span>
              )}
          </div>
        );
      case "transaction_id":
        return <CopyableId id={transaction.transaction_id} maxLength={16} />;
      case "created_at":
        return (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {transaction.created_at
              ? new Date(transaction.created_at).toLocaleString("zh-TW")
              : "-"}
          </span>
        );
      default:
        return null;
    }
  };

  // 僅在冷載入（第一次抓）時顯示 skeleton；後續 refetch 用 overlay
  const showInitialSkeleton =
    (isLoading && !transactionResult) || (isSearching && hasSearched && !searchResults);

  if (showInitialSkeleton) {
    return (
      <AdminPage>
        <AdminPageHeader title="交易記錄" subtitle="查看所有交易記錄" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error || searchError) {
    return (
      <AdminPage>
        <AdminPageHeader title="交易記錄" subtitle="查看所有交易記錄" />
        <AdminErrorAlert
          message={`載入交易記錄時發生錯誤：${
            (error || searchError) instanceof Error
              ? (error || searchError)?.message
              : "未知錯誤"
          }`}
        />
      </AdminPage>
    );
  }

  const overlayVisible =
    (isFetching && !isLoading) || (isSearching && hasSearched);

  return (
    <AdminPage>
      <AdminPageHeader
        title="交易記錄"
        subtitle="查看所有交易記錄"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={isExporting}
              title={hasAnyAppliedFilter ? "匯出所有篩選後的資料" : "匯出所有資料"}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              {isExporting ? "匯出中…" : "匯出交易 CSV"}
            </Button>
          </div>
        }
      />

      {/* 搜尋和篩選 */}
      <div className="space-y-4 mb-6">
        <form onSubmit={handleSearch} className="flex items-center gap-3">
          <AdminSearchInput
            value={searchQuery}
            onChange={(v) => {
              setSearchQuery(v);
              setHasSearched(false);
            }}
            placeholder="搜尋用戶 ID 或交易 ID"
            className="max-w-md"
          />
          <Button type="submit" size="sm" disabled={isSearching || !searchQuery.trim()}>
            {isSearching ? "搜尋中..." : "搜尋"}
          </Button>
          {hasSearched && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery("");
                setHasSearched(false);
              }}
            >
              <X className="h-4 w-4 mr-1" />
              清除
            </Button>
          )}
        </form>

        {/* 日期區間（chip 點下去秒生效，全域共用） */}
        <FinancialDateRangePicker mode="range" />

        {/* 篩選器 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">劇集</label>
            <Select
              value={draftFilters.drama_id || "all"}
              onValueChange={(v) =>
                updateCategoricalFilter({ drama_id: v === "all" ? undefined : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                {dramas.map((d) => (
                  <SelectItem key={d.drama_id} value={d.drama_id}>
                    {d.drama_title || d.drama_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">交易類型</label>
            <Select
              value={currentTypeFilterValue}
              onValueChange={(val) => {
                if (val === "drama_related") {
                  updateCategoricalFilter({ type: undefined, types: [...DRAMA_RELATED_TYPES] });
                } else if (val === "all") {
                  updateCategoricalFilter({ type: undefined, types: undefined });
                } else {
                  updateCategoricalFilter({ type: val as Transaction["type"], types: undefined });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="全部" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="drama_related">劇集相關（解鎖+全劇）</SelectItem>
                {Object.keys(typeConfig).map((type) => (
                  <SelectItem key={type} value={type}>
                    {getTypeLabel(type as Transaction["type"])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 套用 / 清除 按鈕 */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={applyFilters}
            disabled={!hasDirtyFilters || overlayVisible}
          >
            <Check className="h-4 w-4 mr-1" />
            套用篩選
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
            disabled={!hasAnyAppliedFilter && !hasDirtyFilters && !searchQuery}
          >
            <Filter className="h-4 w-4 mr-1" />
            清除篩選
          </Button>
          {hasDirtyFilters && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              篩選條件尚未套用，請點擊「套用篩選」
            </span>
          )}
        </div>
      </div>

      {/* 交易列表 */}
      {displayTransactions && displayTransactions.length > 0 ? (
        <AdminLoadingOverlay visible={overlayVisible} label="資料更新中…">
          <div className="space-y-0">
            <AdminDataTable
              columns={columns}
              data={displayTransactions}
              renderCell={renderCell}
              getRowId={(tx) => tx.id}
            />

            {!hasSearched && (
              <AdminPagination
                page={currentPage}
                onPrev={handlePagePrev}
                onNext={handlePageNext}
                hasMore={hasMore}
                count={displayTransactions.length}
                loading={overlayVisible}
              />
            )}

            {hasSearched && (
              <div className="px-4 py-3 border-t text-xs text-muted-foreground">
                搜尋結果：共 {displayTransactions.length} 筆
              </div>
            )}
          </div>
        </AdminLoadingOverlay>
      ) : (
        <AdminLoadingOverlay visible={overlayVisible} label="資料更新中…">
          <AdminEmptyState
            title={hasSearched ? "未找到符合條件的交易記錄" : "尚無交易記錄"}
          />
        </AdminLoadingOverlay>
      )}
    </AdminPage>
  );
}
